//ICS-206
import React from 'react';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import Fema206Card from 'components/IAP/femaCards/Fema206Card';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

import { getIncidentCardTitle } from '../../USCGPage';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import Ics206SignatureBoxes from '../Ics206SignatureBoxes';
import {
  ICS206_AID_STATIONS_HINT,
  ICS206_BOX_LABELS,
  ICS206_HOSPITALS_HINT,
  ICS206_INCIDENT_LOCATION_HINT,
  ICS206_INCIDENT_NAME_HINT,
  ICS206_OPERATIONAL_PERIOD_HINT,
  ICS206_SPECIAL_PROCEDURES_HINT,
  ICS206_TRANSPORTATION_HINT,
} from '../ics206BoxLabels';
import SaveableField from '../SaveableField';
import { HospitalsTable } from './HospitalsTable';
import { MedicalAidStationsTable } from './MedicalAidStationsTable';
import { MedicalPlanVersionRecording } from './medicalPlanVersionRecording';
import { TransportationsTable } from './TransportationsTable';

const ICS_206_EXPORT_FORMNAME = 'ICS_206_CG';

/**
 * Medical Plan, the ICS 206-CG.
 *
 * Boxes 1-3 are the read-only header; boxes 4-6 are the aid station, ambulance
 * and hospital tables; box 7 is the special procedures; boxes 8 and 9 are the
 * Medical Unit Leader's signature and the Safety Officer's review.
 */
const MedicalPlan: React.FC = () => {
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const formFamily = useIcsFormFamily();
  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const readOnly = !canEditCards || readOnlyForms;

  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const updateIcsInfo = useUpdateIcsInfo();

  // Box 7 writes to `ics_incident_info` from the card itself, outside the
  // provider the three tables record through, so it holds its own recorder.
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_206',
    effectivePeriodNumber
  );

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: ICS_206_EXPORT_FORMNAME,
      data: {},
      mapSketch: null,
      format,
    });
  };

  const exportActions = (
    <FormExportButton
      formname={ICS_206_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-206"
      disabled={isExporting}
    />
  );

  // The FEMA 206 numbers and titles these tables as boxes 3 to 6, which the
  // card renders from the descriptor schema.
  if (formFamily === 'fema') {
    return (
      <MedicalPlanVersionRecording>
        <Fema206Card disabled={!canEditCards} headerActions={exportActions} />
      </MedicalPlanVersionRecording>
    );
  }

  const handleSaveSpecialProcedures = (value: string) => {
    updateIcsInfo.mutate(
      { specialMedicalEmergencyProcedures: value },
      {
        onSuccess: () => {
          if (!readOnlyForms) recordVersionSave.mutate();
        },
      }
    );
  };

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_206" />

      <div className="tw:grid tw:min-w-0 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS206_BOX_LABELS.incidentName}
          testId="ics206-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS206_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics206-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS206_BOX_LABELS.incidentLocation}
          testId="ics206-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS206_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics206-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS206_BOX_LABELS.operationalPeriod}
          testId="ics206-section-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the operational period">
              {ICS206_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics206-field-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS206_BOX_LABELS.aidStations}
        testId="ics206-section-aid-stations"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the aid stations">
            {ICS206_AID_STATIONS_HINT}
          </BoxInfo>
        }
      >
        <MedicalAidStationsTable readOnly={readOnly} />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS206_BOX_LABELS.transportation}
        testId="ics206-section-transportation"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the transportation list">
            {ICS206_TRANSPORTATION_HINT}
          </BoxInfo>
        }
      >
        <TransportationsTable readOnly={readOnly} />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS206_BOX_LABELS.hospitals}
        testId="ics206-section-hospitals"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the hospital list">
            {ICS206_HOSPITALS_HINT}
          </BoxInfo>
        }
      >
        <HospitalsTable readOnly={readOnly} />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS206_BOX_LABELS.specialProcedures}
        testId="ics206-section-special-procedures"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About special medical emergency procedures">
            {ICS206_SPECIAL_PROCEDURES_HINT}
          </BoxInfo>
        }
      >
        <SaveableField
          id="ics206-special-procedures"
          label={ICS206_BOX_LABELS.specialProcedures}
          hideLabel
          multiline
          rows={4}
          placeholder="Describe special medical emergency procedures for this incident..."
          disabled={readOnly}
          value={incidentInfo?.specialMedicalEmergencyProcedures ?? ''}
          onSave={handleSaveSpecialProcedures}
        />
      </Ics204CardSection>

      <Ics206SignatureBoxes periodNumber={effectivePeriodNumber} />
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return (
      <MedicalPlanVersionRecording>
        <div className="dark content tw:text-foreground mt-2">{body}</div>
      </MedicalPlanVersionRecording>
    );
  }

  return (
    <MedicalPlanVersionRecording>
      <Card
        data-testid="uscg-206-card"
        data-tour="ics206-card"
        data-form-family="uscg"
        className="dark tw:bg-background tw:text-foreground"
      >
        <CardHeader>
          <CardTitle>{getIncidentCardTitle('medicalPlan')}</CardTitle>
          <CardAction>{exportActions}</CardAction>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    </MedicalPlanVersionRecording>
  );
};

export default MedicalPlan;
